/**
 * #1071 — the highest-volume initial spread/total line
 * (`docs/261007Issue1071HighestVolumeInitialLine.md`). The board and Game
 * Detail (`GameDetail/initialLine.ts`) consult it behind `initialLineConfig.ts`'s
 * switch — and, while that is off, the board only to count what it would have
 * chosen.
 *
 * Pure. Ranks the three-line window around today's closest-to-50¢ line on
 * each line's own venue's observed lifetime traded volume, and answers only
 * when every eligible line in that window has reported. Anything but a `line`
 * answer keeps today's default (`nearestToFiftyIndex`).
 */
import type { SpreadLine, TotalLine } from '../ValueDashboard/types';
import { tradedVolume } from '@/lib/market/tickerScalars';
import { classifyLineAvailability } from '@/lib/sport/lineAvailability';
import { linePriceCents, primaryLineBook, venueAwareLineWindow } from './synthesize';

/** What the volume rule says about one row.
 *  - `line`: the busiest eligible line, as an index.
 *  - `pending`: not decidable yet — an eligible line's volume is not
 *    observed, or there is no 50¢ line yet to build the window around (no
 *    anchor; #780: such a row stays unseeded). Ask again.
 *  - `fallback`: the answer is final and it is today's 50¢ line, for `reason`
 *    (nothing traded, the busiest line has no price, no eligible line, or an
 *    anchor that is not an index into `lines`). */
export type VolumeLineAnswer =
  | { kind: 'line'; idx: number }
  | { kind: 'pending' }
  | { kind: 'fallback'; reason: 'none-traded' | 'busiest-unpriced' | 'no-eligible' | 'bad-anchor' };

/** The volume rule over `lines`.
 *
 *  Eligibility matches `nearestToFiftyIndex` (#1053 Part 1, `1ac41e4a7`): the
 *  lines carrying a Kalshi book when any line does, else every line. A Kalshi
 *  line whose book is suspended or a mapping error
 *  (`classifyLineAvailability`) is not eligible. An unpriced line still has
 *  to report and still ranks, but it is never the answer (#780) — a busier
 *  line that cannot be priced yet must not hand the default to a quieter one.
 *  Each line is ranked on that venue's book only; venue volumes are never
 *  compared or summed. Ties go to the line priced nearer 50¢, then to the
 *  lower strike (the lower `line` value; signed for spreads), so the answer
 *  does not depend on the order of `lines`. */
export function highestVolumeLine(
  lines: ReadonlyArray<SpreadLine | TotalLine>,
): VolumeLineAnswer {
  const kalshiOnly = lines.some((line) => line.kalshi != null);
  let eligible = 0;
  let bestIdx: number | null = null;
  let bestVolume = 0;
  let bestDist = Infinity;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (kalshiOnly) {
      if (line.kalshi == null) continue;
      const availability = classifyLineAvailability(line.kalshi);
      if (availability === 'suspended' || availability === 'mapping-error') continue;
    }
    eligible++;
    const volume = tradedVolume(primaryLineBook(line));
    if (volume == null) return { kind: 'pending' };
    if (volume <= 0) continue;
    const cents = linePriceCents(line);
    const dist = cents == null || !Number.isFinite(cents) ? Infinity : Math.abs(cents - 50);
    const better = bestIdx == null
      || volume > bestVolume
      || (volume === bestVolume && (dist < bestDist || (dist === bestDist && line.line < lines[bestIdx].line)));
    if (better) {
      bestIdx = i;
      bestVolume = volume;
      bestDist = dist;
    }
  }
  if (eligible === 0) return { kind: 'fallback', reason: 'no-eligible' };
  if (bestIdx == null) return { kind: 'fallback', reason: 'none-traded' };
  if (bestDist === Infinity) return { kind: 'fallback', reason: 'busiest-unpriced' };
  return { kind: 'line', idx: bestIdx };
}

/** The volume rule within the three-line window around `anchorIdx` — the
 *  caller passes `nearestToFiftyIndex(lines)` — with `line` indices into
 *  `lines`. The window is the board's own (`venueAwareLineWindow`), so a
 *  strike two or more steps from even money is never chosen, and
 *  completeness covers those lines only. No anchor (no 50¢ line yet) is
 *  `pending`; an anchor that is not an index into `lines` is `bad-anchor`,
 *  never a clamped guess. */
export function windowedVolumeLine(
  lines: ReadonlyArray<SpreadLine | TotalLine>,
  anchorIdx: number | null,
): VolumeLineAnswer {
  if (anchorIdx == null) return { kind: 'pending' };
  if (!Number.isInteger(anchorIdx) || anchorIdx < 0 || anchorIdx >= lines.length) {
    return { kind: 'fallback', reason: 'bad-anchor' };
  }
  const window = venueAwareLineWindow(lines, anchorIdx);
  const answer = highestVolumeLine(window.map((i) => lines[i]));
  return answer.kind === 'line' ? { kind: 'line', idx: window[answer.idx] } : answer;
}

/** Why a row was seeded where it was. `volume` = the rule's busiest line
 *  (`atFifty`: it is also the 50¢ line); `fallback` = the rule's final answer
 *  was the 50¢ line; `limit` = the window's volume was still incomplete when
 *  the wait ran out, so the 50¢ line was seeded. */
export type InitialLineOutcome =
  | { kind: 'volume'; atFifty: boolean }
  | { kind: 'fallback'; reason: 'none-traded' | 'busiest-unpriced' | 'no-eligible' }
  | { kind: 'limit' };

/** One seeding pass over one row.
 *  - `unanchored`: no 50¢ line yet — the row stays unseeded and no wait
 *    starts (#780). An anchor outside `lines` lands here too: no line can be
 *    named, so nothing is seeded.
 *  - `wait`: the window's volume is incomplete and the wait has not run out —
 *    show the 50¢ line without storing it, start the wait if it has not
 *    started, and ask again.
 *  - `seed`: store `lines[idx]` once, for `outcome`. */
export type InitialLineStep =
  | { kind: 'unanchored' }
  | { kind: 'wait' }
  | { kind: 'seed'; idx: number; outcome: InitialLineOutcome };

/** The seeding decision for one row: `anchorIdx` is the caller's
 *  `nearestToFiftyIndex(lines)`, `waitedMs` how long this row has shown its
 *  50¢ line waiting (null: not started), `waitMs` the limit. */
export function initialLineStep(
  lines: ReadonlyArray<SpreadLine | TotalLine>,
  anchorIdx: number | null,
  waitedMs: number | null,
  waitMs: number,
): InitialLineStep {
  const answer = windowedVolumeLine(lines, anchorIdx);
  if (anchorIdx == null) return { kind: 'unanchored' };
  if (answer.kind === 'line') {
    return { kind: 'seed', idx: answer.idx, outcome: { kind: 'volume', atFifty: answer.idx === anchorIdx } };
  }
  if (answer.kind === 'fallback') {
    return answer.reason === 'bad-anchor'
      ? { kind: 'unanchored' }
      : { kind: 'seed', idx: anchorIdx, outcome: { kind: 'fallback', reason: answer.reason } };
  }
  return waitedMs != null && waitedMs >= waitMs
    ? { kind: 'seed', idx: anchorIdx, outcome: { kind: 'limit' } }
    : { kind: 'wait' };
}
